/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  STATION,
  STATION_SELECTION_SOURCE,
  SnapUserType,
} from '../configs/constants';
export type {
  SnapBookingConfirmationFlightJourney,
  SnapBookingConfirmationGuest,
  SnapBookingConfirmationHotelItinerary,
  SnapBookingConfirmationLeg,
  SnapBookingConfirmationResponse,
  SnapBookingConfirmationSegment,
} from './snapBookingConfirmation';

/**
 * Station data from /flights/station/v3/{locale}/file.json
 */
export interface Station {
  Lat: number;
  Long: number;
  Dest: string;
  PinYin: string;
  AAFlight: boolean;
  CityCode: string;
  CityName: string;
  TimeZone: string;
  AirportName: string;
  CountryCode: string;
  CountryName: string;
  StationCode: string;
  StationName: string;
  AlternativeName: string;
  StationType: string;
  Tag: string;
  Weightage: number;
}

/**
 * Destination data from /flights/v3/destination/{code}/file.json
 */
export interface Destination {
  AAFlight: boolean;
  CityCode: string;
  CityName: string;
  Provider: string;
  SupportedAirlineProfiles: string;
  MaxDuration: string;
  MinDuration: string;
}

/**
 * Map of destination code to destination data
 */
export type DestinationMap = {
  [key: string]: {
    [code: string]: Destination;
  };
};

/**
 * Recent search entry with from/to stations and timestamp
 */
export interface RecentSearch {
  id: string;
  from: Station;
  to: Station;
  fromIsAllAirports?: boolean;
  toIsAllAirports?: boolean;
  timestamp: number;
}

export type SearchStationsViewHandle = {
  submit: () => void;
};

export type StationSelectionPayload = {
  from: Station | null;
  to: Station | null;
  fromIsAllAirports?: boolean;
  toIsAllAirports?: boolean;
};

export type SearchStationsViewProps = {
  onSelect?: (params: StationSelectionPayload) => void;
  isLoading?: boolean;
  testID?: string;
  showSearchButton?: boolean;
  originStation?: Station | null;
  destinationStation?: Station | null;
  errors?: {
    origin?: string;
    destination?: string;
  };
};

export type TripTypeToggleProps = {
  tripType: TripType;
  onTripTypeChange: (tripType: TripType) => void;
};

export enum TripType {
  ROUND_TRIP = 'ROUNDTRIP',
  ONE_WAY = 'ONEWAY',
}
export interface SelectedDates {
  depart: Date | undefined;
  return: Date | undefined;
}

export interface HotelDates {
  checkIn: Date | undefined;
  checkOut: Date | undefined;
}

/**
 * Search result with match information for highlighting
 */
export interface StationSearchResult {
  station: Station;
  matchField: 'cityName' | 'stationCode' | 'airportName' | 'tag';
  matchIndices: [number, number][];
}

/**
 * Grouped stations by city for hierarchical display
 */
export interface GroupedStation {
  cityCode: string;
  cityName: string;
  countryName: string;
  airports: Station[];
}

/**
 * Station search field type
 */
export type StationType = (typeof STATION)[keyof typeof STATION];

export type StationSelectionSource =
  (typeof STATION_SELECTION_SOURCE)[keyof typeof STATION_SELECTION_SOURCE];

/**
 * Station list hook typing
 */
export type TranslationFn = (key: string) => string;

// ── LFC (Low Fare Calendar) Types ───────────────────────────────────────────

/**
 * Phoenix DatePicker price object per date.
 * Keys use the locale-dependent format from `toLocaleDateString`.
 */
export interface DatePriceObject {
  displayPriceString: string;
  price: number;
  soldOut?: boolean;
  noFlights?: boolean;
}

/**
 * Map of date key to price data for a single month.
 */
export interface MonthPriceObject {
  [dateKey: string]: DatePriceObject;
}

/**
 * Array of MonthPriceObject, one per month displayed in the calendar.
 */
export type CalendarPriceObject = MonthPriceObject[];

export interface LfcFlightData {
  departureDate: string;
  price: number;
  shortFormattedPrice: string;
  shortPrice: string;
  airlineProfile: string;
  aaFlight: boolean;
  soldOut: boolean;
  noFlights: boolean;
}

export interface LfcResponse {
  currency: string;
  origin: string;
  destination: string;
  airlineProfiles: string[];
  cabinClass: string;
  dateRange: string;
  data: LfcFlightData[];
}

export type LfcDateChunk = {
  beginDate: string;
  endDate: string;
  key: string;
};

export type LfcChunkResult = {
  chunk: LfcDateChunk;
  data: LfcFlightData[];
  isError: boolean;
  isFetched: boolean;
  isLoading: boolean;
};

/**
 * Parameters for fetching low fare calendar data
 */
export interface LfcParams {
  departStation: string;
  arrivalStation: string;
  beginDate: string;
  endDate: string;
  currency: string;
  airlineProfile: string;
  userType?: SnapUserType;
}

/**
 * Parameters for the useSnapLfc hook
 */
export interface UseSnapLfcParams {
  originCode: string;
  destinationCode: string;
  currency: string;
  tripType: 'round-trip' | 'one-way';
  userType?: SnapUserType;
  visibleMonths?: Date[][];
}

/**
 * Return type for the useSnapLfc hook
 */
export interface UseSnapLfcResult {
  departChunks: LfcChunkResult[];
  returnChunks: LfcChunkResult[];
  isLoading: boolean;
  isError: boolean;
}

export type StationSearchIndexLike = {
  search: (query: string) => Station[];
} | null;

export interface UseStationListDataParams {
  query: string;
  stations: Station[];
  searchIndex?: StationSearchIndexLike;
  recentStations: Array<{ station: Station; isAllAirports: boolean }>;
  validDestinationCodes?: string[];
  idPrefix?: string;
  t: TranslationFn;
}

export interface SearchFormState {
  tripType: TripType;
  selectedDates: SelectedDates;
  hotelDates: HotelDates;
  selectDifferentDates: boolean;
  rooms: RoomConfig[];
  originStation: Station | null;
  destinationStation: Station | null;
  fromIsAllAirports?: boolean;
  toIsAllAirports?: boolean;
}

export type SnapListingParams = {
  tripType?: 'R' | 'O' | string;
  orig?: string;
  dest?: string;
  fromDateFlight?: string;
  toDateFlight?: string;
  fromDateHotel?: string;
  toDateHotel?: string;
  rooms?: string | number;
  adults?: string;
  children?: string;
  infants?: string;
  country_code?: string;
  currency?: string;
  language?: string;
  page_number?: number;
  airlineProfile?: string;
  departFlightId?: string;
  returnFlightId?: string;
  snapId?: string;
  Usermailid?: string;
  tier?: string;
  gaid?: string;
  hotelSize?: number;
  guest_rating?: string;
  guestRating?: string;
  starRating?: string | string[];
  star_rating?: string | string[];
  propertyTypes?: string | string[];
  category?: string | string[];
  toLocationId?: string | number;
};

export interface ChildGuest {
  id: string;
  age: number;
}
export interface RoomConfig {
  id: string;
  adults: number;
  children: ChildGuest[];
  infants?: number;
}

/**
 * Recent search entry stored via React Query persistence.
 * Keyed by from+to StationCode; same route updates instead of creating new (Scenario 4).
 * Hotel dates are intentionally excluded from the card display (Scenario 7).
 */
export interface SnapRecentSearchEntry {
  id: string;
  from: Station;
  to: Station;
  fromIsAllAirports?: boolean;
  toIsAllAirports?: boolean;
  tripType: TripType;
  departDate: string; // ISO date string (yyyy-MM-dd)
  returnDate?: string; // ISO date string (yyyy-MM-dd)
  hotelCheckIn?: string; // stored but NOT shown on the card
  hotelCheckOut?: string; // stored but NOT shown on the card
  selectDifferentDates: boolean;
  rooms: RoomConfig[];
  timestamp: number;
}

/**
 * Store structure for snap recent searches (React Query cache)
 */
export interface SnapRecentSearchesStore {
  recentSearches: SnapRecentSearchEntry[];
}

export interface SnapSearchViewFlags {
  snap_recent_searches_enabled: boolean;
  snap_one_way_enabled: boolean;
  snap_search_banner: boolean;
  snap_lfc_enabled: boolean;
  snap_bkk_dmk_mapping_enabled: boolean;
  snap_recent_search_max_count: number;
  /** Renders depart and return as one combined date field. */
  snap_combined_date_field_enabled: boolean;
  hotel_snap_unified_listing_enabled: boolean;
  /** When on, listing price filters compare the total guest price (`baseRate`). */
  snap_total_guest_price_enabled: boolean;
  /** Shows the SNAP listing filter. Each section also requires its own flag. */
  snap_list_filter_enabled: boolean;
  snap_price_filter_enabled: boolean;
  snap_promotion_filter_enabled: boolean;
  snap_amenities_filter_enabled: boolean;
  snap_star_rating_filter_enabled: boolean;
  snap_guest_rating_filter_enabled: boolean;
  snap_property_type_filter_enabled: boolean;
  snap_neighbourhood_filter_enabled: boolean;
  snap_location_filter_enabled: boolean;
  snap_enable_portal_usage: boolean;
  hotel_list_default_page_size: number;
}

export interface SnapCheckoutClientSsoDetails {
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  custNo?: string;
  clientId?: string;
  environment?: string;
}

export interface SnapCheckoutClientInfo {
  gaId?: string;
  agentId?: string;
  ssoDetails?: SnapCheckoutClientSsoDetails;
}

export interface SnapCheckoutFlightDetail {
  departFlightId?: string;
  flightDepartDate?: string;
  airlineProfile?: string;
  returnFlightId?: string;
  flightReturnDate?: string;
}

export interface SnapCheckoutHotelDetail {
  hotelId?: string;
  'room-Id'?: string;
  roomId?: string;
  'rate-id'?: string;
  rateId?: string;
  'rate-option-id'?: string;
  rateOptionId?: string;
  'bedgroup-id'?: string;
  bedgroupId?: string;
  'hotel-name'?: string;
  hotelName?: string;
  'checkin-time'?: string;
  checkinTime?: string;
  'checkout-time'?: string;
  checkoutTime?: string;
  'checkin-date'?: string;
  checkinDate?: string;
  'checkout-date'?: string;
  checkoutDate?: string;
  hotelCountryCode?: string;
  rooms?: number;
}

export interface SnapCheckoutAncillaryBaggageDimension {
  height?: { value: number | null; unit: string };
  length?: { value: number | null; unit: string };
  width?: { value: number | null; unit: string };
  weight?: { value: number | null; unit: string };
  baggageCode?: string;
}

export interface SnapCheckoutAncillaryItem {
  id: string;
  properties?: {
    deleteAncillary?: unknown;
    baggageDimension?: SnapCheckoutAncillaryBaggageDimension[];
    indices?: number[];
    feeCode?: string | null;
    limitPerPassenger?: string | null;
    name?: string | null;
    count?: number | null;
  };
}

export interface SnapCheckoutTravellerDetail {
  gender?: string;
  firstName?: string;
  lastName?: string;
  dateOfbirth?: string;
  nationality?: string;
  category?: 'ADT' | 'CHD' | 'INFT';
  specialRequest?: string[];
  bigShotId?: string;
  customerNumber?: string;
  'room-number'?: number;
  roomNumber?: number;
  primary?: boolean;
  travelDocuments?: unknown[];
  ancillaryItems?: {
    baggage?: {
      departFlight?: SnapCheckoutAncillaryItem[];
      returnFlight?: SnapCheckoutAncillaryItem[];
    };
  };
  smoking?: boolean;
}

export interface SnapCheckoutBookerDetail {
  firstName?: string;
  lastName?: string;
  email?: string;
  countryCode?: string;
  dialCode?: string;
  mobileNumber?: string;
  loyaltyId?: string;
}

export interface SnapCheckoutOccupancy {
  adults?: string | number;
  children?: string | number;
  infants?: string | number;
}

export interface SnapCheckoutRequest {
  orig: string;
  dest: string;
  currency: string;
  language: string;
  clientInfo?: SnapCheckoutClientInfo;
  countryCode: string;
  flightDetail: SnapCheckoutFlightDetail;
  hotelDetail: SnapCheckoutHotelDetail;
  firebaseId?: string;
  travellerDetail: SnapCheckoutTravellerDetail[];
  tripType: 'R' | 'O';
  bookerDetail: SnapCheckoutBookerDetail;
  occupancy?: SnapCheckoutOccupancy;
  gaId?: string;
}

export interface SnapCheckoutResponse {
  paymentUrl?: string;
  payment_url?: string;
  bookingRequestId?: string;
  [key: string]: unknown;
}

export type {
  SnapPageDataAncillaryItem,
  SnapPageDataFlightDetails,
  SnapPageDataPassenger,
  SnapPageDataPriceBreakdown,
  SnapPageDataRequest,
  SnapPageDataResponse,
  SnapPageDataSso,
  SnapPageDataUserContext,
  SnapPassengerAncillaries,
} from './snapPageData';
export type {
  SnapAncillarySummaryHotelDetails,
  SnapAncillarySummaryResponse,
} from './snapAncillarySummary';
